import { forwardRef, TextareaHTMLAttributes } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { layout, LayoutProps } from "styled-system";

import { TextareaProps } from "./textarea.type";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";

const StyledTextarea = styled.textarea<
  TextareaHTMLAttributes<HTMLTextAreaElement> &
    Omit<LayoutProps, "width"> & {
      error?: boolean;
      warning?: boolean;
      resizable?: boolean;
    }
>`
  box-sizing: border-box;
  height: 68px;
  width: 100%;
  outline: none;
  ${css({
    borderRadius: 2,
    fontSize: "body",
    fontFamily: "normal",
    border: "normalBorder",
    boxShadow: "default",
    padding: 3,
    "&:focus": {
      border: "primaryBorder",
      boxShadow: "accent",
    },
  })};
  ${(props) =>
    props.error
      ? css({
          border: "errorBorder",
          boxShadow: "error",
          "&:focus": {
            boxShadow: "error",
            border: "errorBorder",
          },
        })
      : null}
  ${(props) =>
    props.warning
      ? css({
          border: "warningBorder",
          boxShadow: "warning",
          "&:focus": {
            boxShadow: "warning",
            border: "warningBorder",
          },
        })
      : null}
  ${(props) =>
    !props.resizable
      ? css({
          resize: "none",
        })
      : null}
      ${layout}
`;

const StyledLabel = styled(Typography)<{ required?: boolean }>`
  ${css({
    fontWeight: "medium",
    fontSize: "body-small",
    height: "18px",
  })}
  ${(props) =>
    props.required
      ? css({
          "&:after": {
            color: "primary-red",
            content: `"*"`,
          },
        })
      : null}
`;

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      value,
      label,
      children,
      width,
      paddingX,
      required,
      error,
      warning,
      onChange,
      ...props
    }: TextareaProps,
    ref
  ) => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        paddingX={paddingX}
        width={width}
      >
        <StyledLabel required={required}>{label}</StyledLabel>
        <StyledTextarea
          error={error}
          warning={warning}
          required={required}
          ref={ref}
          onChange={onChange}
          aria-label={label}
          value={value}
          {...props}
        />
        {children}
      </Box>
    );
  }
);

export default Textarea;
